import { featureCollection } from "@turf/helpers";
import { Localization } from "@viamap/viamap2-common";
import {
  DataDivisionType,
  DivisionStylingType,
  LayerInfo,
  LayerInfoStyling,
} from "src/common/managers/Types";
import { LayerFunc } from "src/managers/LayerFunc";
import { ReferenceGeomDenmark } from "src/managers/ReferenceGeomDenmark";
import JPBRegions from "./JPBRegions.json";
import { JPBManager } from "./JPBManager";


const landsdelLayerColors = [
  "#0f6c81",
  "#c45135",
  "#2f8f70",
  "#725ea8",
  "#a77916",
  "#c23d7a",
  "#4f7fbd",
  "#8a5a44",
  "#6f7f2a",
  "#00796b",
  "#7b1fa2",
];

type MunicipalityMeta = {
  landsdel: string;
  kommunenr: number;
  kommunenavn: string;
};

function getMunicipalityMetaByCode(): Map<number, MunicipalityMeta> {
  const map = new Map<number, MunicipalityMeta>();
  const municipalities = (JPBRegions as any).municipalities as Array<{
    landsdel?: string;
    kommunenr?: number;
    kommunenavn?: string;
  }>;

  municipalities.forEach((municipality) => {
    if (
      !municipality.landsdel ||
      !municipality.kommunenavn ||
      municipality.kommunenr === undefined
    ) {
      return;
    }

    map.set(municipality.kommunenr, {
      landsdel: JPBManager.getMolbakDisplayLandsdel(municipality.landsdel),
      kommunenr: municipality.kommunenr,
      kommunenavn: municipality.kommunenavn,
    });
  });

  return map;
}

export function getMunicipalityFeatures(kommunenrList: number[]): any[] {
  const refGeo = ReferenceGeomDenmark.getReferenceGeo();
  const features = (refGeo as any)?.municipality?.features;
  if (!Array.isArray(features)) return [];
  const codeSet = new Set(kommunenrList.map((code) => String(code)));
  return features.filter((feature) =>
    codeSet.has(
      String(feature?.properties?.Komnr ?? feature?.properties?.KOMKODE ?? ""),
    ),
  );
}

function buildLandsdelStyling(landsdele: string[], featureCount: number): LayerInfoStyling {
  return {
    color: landsdelLayerColors[0],
    opacity: 1,
    areaFillOpacity: 0.42,
    colorByProperty: "Landsdel",
    colorByValue: {
      useColorRange: 3,
      dataColumn: 0,
      divisions: {
        type: DataDivisionType.Discrete,
        list: landsdele.map((landsdel, index) => ({
          from: null,
          to: null,
          value: landsdel,
          count: featureCount,
          color: landsdelLayerColors[index % landsdelLayerColors.length],
        })),
        otherValuesCount: 0,
      },
      divisionStylingType: DivisionStylingType.Custom,
    },
    outsideColorRange: {
      show: true,
      color: "lightgray",
    },
  };
}

export function buildMolbakLandsdelMapLayer(): LayerInfo | null {
  const municipalityMetaByCode = getMunicipalityMetaByCode();
  if (!municipalityMetaByCode.size) return null;

  const features = getMunicipalityFeatures([...municipalityMetaByCode.keys()]).map(
    (feature) => {
      const kommunenr = Number.parseInt(
        String(feature?.properties?.Komnr ?? feature?.properties?.KOMKODE ?? ""),
        10,
      );
      const meta = municipalityMetaByCode.get(kommunenr);
      if (!meta) return null;

      return {
        ...feature,
        properties: {
          ...feature.properties,
          Landsdel: meta.landsdel,
          Kommune: meta.kommunenavn,
          Kommunenr: meta.kommunenr,
        },
      };
    },
  ).filter(Boolean);

  if (!features.length) return null;

  const landsdele = [...new Set(features.map((feature) => feature.properties.Landsdel))]
    .sort((a, b) => a.localeCompare(b, "da"));
  const fc = featureCollection(features as any);
  const layer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
    fc,
    Localization.getText("JPB Mølbak landsdele"),
  );

  layer.propertiesInGeoJson = ["Landsdel", "Kommune", "Kommunenr", "Kilde"];
  layer.propertiesToDisplay = [
    { name: "Landsdel" },
    { name: "Kommune" },
    { name: "Kommunenr" },
    { name: "Kilde" },
  ];
  layer.styling = buildLandsdelStyling(landsdele, features.length);

  return layer;
}

export async function createMolbakLandsdelMapLayer(): Promise<LayerInfo | null> {
  await ReferenceGeomDenmark.loadReferenceGeo();
  return buildMolbakLandsdelMapLayer();
}
